import { useEffect, useState } from 'react';
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Modal, Button, Card, CardHeader, CardBody, Row, Col, ButtonToolbar } from 'reactstrap';
import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';
import axios from "axios";
import { useDispatch, flashSuccess, flashError } from "@/shared/components/flash"
import { Field, Form } from 'react-final-form';
import { FieldArray } from "react-final-form-arrays"
import arrayMutators from "final-form-arrays"
import { fetchRoles } from "@/shared/apiHelper"
import CheckboxGroup from "@/shared/components/form/CheckboxGroup"
import renderRadioButtonField from '@/shared/components/form/RadioButton';

const copyOrMove = [
    { "id": "copy_user", "name": "Copy User" },
    { "id": "move_user", "name": "Move User" }
]
const MoveUserModal = ({ program, isOpen, toggle, theme, rtl, user, setTrigger }) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [programList, setProgramList] = useState([]);

    const getRoles = (organizationId) => {
        setLoading(true);
        fetchRoles(organizationId, 1)
            .then(data => {
                setRoles(data);
                setLoading(false);
            })
    }

    useEffect(() => {
        getRoles(program.organization_id);
    }, [program]);

    useEffect(() => {
        fetchPrograms();
    }, []);

    const fetchPrograms = async () => {
        try {
            const response = await axios.get(
                `/organization/${program.organization_id}/program/${program.id}/top-bottom-hierarchy`,
            );
            setProgramList(response.data);

        } catch (e) {
            throw new Error(`API error:${e?.message}`)
        }
    }

    const onSubmit = (values) => {
        let payload = {
            "roles": values.roles ?? [],
            "programs": values.program,
        }
        if (values.copy_or_move.includes('move_user')) {
            payload.move_user = 1;
        } else {
            payload.copy_user = 1;
        }
        setLoading(true)
        const url = `/organization/${program.organization_id}/program/${program.id}/user/${user.id}/copy_move_user`;
        axios.post(url, payload)
            .then((res) => {
                if (res.status === 200) {
                    toggle();
                    setTrigger(Math.floor(Date.now() / 1000))
                    flashSuccess(dispatch, "User moved/copied successfully", "success");
                }
            })
            .catch(error => {
                flashError(dispatch, error.response.data)
            })
            .finally(() => setLoading(false))
    }

    return (
        <Modal className={`modal-action modal-lg ${theme.className} ${rtl.direction}-support`} isOpen={isOpen} toggle={toggle}>
            <Card className='w-100'>
                <CardBody>
                    <Form
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={{ roles: user.roles ? user.roles.map(r => r.id) : [], copy_or_move: 'copy_user' }}
                        mutators={{
                            ...arrayMutators
                        }}
                    >
                        {({ handleSubmit }) => (
                            <form className="form" onSubmit={handleSubmit}>
                                <Row className='w100'>
                                    <Col md="6" lg="6" xl="6">
                                        <h3 className="mb-4">Copy/Move User</h3>
                                    </Col>
                                    <Col md="6" lg="6" xl="6" className='text-right'>
                                        <ButtonToolbar className="modal__footer flex justify-content-right w100">
                                            <Button outline color="primary" className="mr-3" onClick={toggle}>Close</Button>{' '}
                                            <Button type="submit" disabled={loading} className="btn btn-primary" color="#ffffff">Save</Button>
                                        </ButtonToolbar>
                                    </Col>
                                </Row>
                                <Row className='mx-auto text-left'>
                                    <Col>
                                        {copyOrMove.map((copy) =>
                                            <Field
                                                name="copy_or_move"
                                                component={renderRadioButtonField}
                                                label={copy.name}
                                                radioValue={String(copy.id)}
                                                key={`${copy.id}`}
                                            />
                                        )}
                                    </Col>
                                </Row>
                                <Row className='mx-auto text-left'>
                                    <Col>
                                        <Field name='roles'>
                                            {({ input, meta }) => (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Roles</span>
                                                    <div className="form__form-group-field">
                                                        <div className="form__form-group-row">
                                                            <FieldArray
                                                                component={CheckboxGroup}
                                                                options={roles}
                                                                {...input}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <span className="form__form-group-error">
                                                                    {meta.error}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Field>
                                    </Col>
                                </Row>
                                <Row className='mx-auto text-left'>
                                    <Col>
                                        <Field name='program'>
                                            {({ input, meta }) => (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Select Programs</span>
                                                    <div className="form__form-group-field">
                                                        <div className="form__form-group-row">
                                                            <FieldArray
                                                                component={CheckboxGroup}
                                                                options={programList}
                                                                {...input}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <span className="form__form-group-error">
                                                                    {meta.error}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Field>
                                    </Col>
                                </Row>
                            </form>
                        )}
                    </Form>
                </CardBody>
            </Card>
        </Modal>
    )
}

const validate = values => {
    let errors = {};
    if (!Array.isArray(values.roles) || values.roles.length === 0) {
        errors.roles = 'At least one role must be selected';
    }
    if (!Array.isArray(values.program) || values.program.length === 0) {
        errors.program = 'At least one program must be selected';
    }
    return errors;
}

MoveUserModal.propTypes = {
    theme: ThemeProps.isRequired,
    rtl: RTLProps.isRequired
};

export default withRouter(connect((state) => ({
    auth: state.auth,
    theme: state.theme,
    rtl: state.rtl,
}))(MoveUserModal));
